import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import { ENTITY_TYPE_AGENCY, ENTITY_TYPE_VENDOR, REACT_APP_ACR_API_BASE_URL, REACT_APP_ACR_API_PROTOCOL, JWT_TOKEN_KEY, ACCESS_LEVEL_SUPER_USER, ACCESS_LEVEL_ADMIN, IMPERSONATION_SESSION_KEY, HOME_URL } from "../utlities/core.constants";
import isActiveAccount from "../utlities/accountStatus";
import useEntity from "../hooks/useEntity.hook";
import usePrimaryPoc from "../hooks/usePrimaryPoc.hook";
import { formatPrimaryPoc } from "../utlities/common.funcs";
import { getRouter } from "../utlities/routerRegistry";
import {
  generateLoginUrl,
  shouldReauthenticateOnExpiry,
  readLastReauthAttemptMs,
  markReauthAttempt,
  clearReauthAttempt,
} from "../utlities/auth.utility";

// When several authenticated requests are in flight and all return 401 at once (e.g. a page that
// fires multiple API calls), only the first should trigger the re-auth redirect; the rest are
// ignored while the browser is already navigating away. Reset naturally on page reload.
let reauthRedirectInFlight = false;

export const UserContext = React.createContext({})

/**
 * Identity the access helpers should authorize. While a SuperUser is in Support Mode the
 * presented user is an admin of the target org, so the real SUPERUSER short-circuit must not apply.
 * The real token still authorizes API calls; this only stops the UI from treating Support Mode as
 * unscoped SuperUser reach.
 */
export function presentedIdentity(user, impersonation) {
  if (!user || !impersonation || !isActiveAccount(user) || user.accessLevel !== ACCESS_LEVEL_SUPER_USER) {
    return user;
  }

  const ownerType = impersonation.ownerType?.toLowerCase();
  return {
    ...user,
    vendorId: ownerType === ENTITY_TYPE_VENDOR ? impersonation.ownerId : null,
    agencyId: ownerType === ENTITY_TYPE_AGENCY ? impersonation.ownerId : null,
    accessLevel: ACCESS_LEVEL_ADMIN,
  };
}

// eslint-disable-next-line no-unused-vars
export const UserConsumer = UserContext.Consumer;

// eslint-disable-next-line react/prop-types
export function UserProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [initialUserStorageCheck, setInitialUserStorageCheck] = useState(false);
  const [initialUserCallbackCheck, setInitialUserCallbackCheck] = useState(false);
  const [userEntity, setUserEntity] = useState(null);
  const [request, setRequest] = useState(null);
  const { getUserVendor, getUserAgency, getVendor, getAgency } = useEntity();
  const { getPrimaryPoc } = usePrimaryPoc();
  const [isVendorUser, setIsVendorUser] = useState(false);
  const [isAgencyUser, setIsAgencyUser] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [hasPendingRequestAccess, setHasPendingRequestAccess] = useState(false);
  // Active SuperUser impersonation session ({ ownerType, ownerId, ownerName }) or null. The server
  // (GET /api/impersonation/current) is authoritative; sessionStorage is only an optimistic cache.
  const [impersonation, setImpersonation] = useState(null);
  const tokenRef = useRef(null);

  const impersonationApiBase = `${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}/api/impersonation`;

  const setJwt = (jwt) => {
    tokenRef.current = jwt || null;
    axios.interceptors.request.eject(0);

    if (!jwt) {
      setToken(null);
      setUser(null);
      localStorage.setItem(JWT_TOKEN_KEY, "");
      setInitialUserStorageCheck(true);
      return;
    }
    setAuthError(null);

    axios.defaults.validateStatus = (status) => {
      return status >= 200 && status < 500; // default
    };

    axios.interceptors.request.use((config) => {
      const updatedConfig = { ...config }
      updatedConfig.headers.Authorization = 'Ignore';
      if (jwt && jwt.id_token && config.url.includes(REACT_APP_ACR_API_BASE_URL)) {

        updatedConfig.headers.Authorization = `Bearer ${jwt.id_token}`;
      }

      return updatedConfig;
    }, (error) => {
      // Do something with request error
      return Promise.reject(error);
    });

    localStorage.setItem(JWT_TOKEN_KEY, JSON.stringify(jwt));

    setToken(jwt);
  }

  useEffect(() => {
    axios.interceptors.response.use((response) => {
      if (response.status === 401) {
        // A 401 on an active session almost always means the Login.gov id_token expired while the
        // user was working, even though their Login.gov SSO session is usually still valid. Rather
        // than silently dropping them on Home (losing their place mid-task), re-authenticate
        // silently: redirect through Login.gov, which returns immediately when SSO is active and
        // sends the user back to the exact page they were on (state = current pathname). In-progress
        // work such as the ACR editor draft persists in localStorage across the redirect.
        //
        // If there was never a session, leave the user on whatever public page they're on.
        const hadActiveSession = !!tokenRef.current;
        setJwt(null);

        // Ignore additional concurrent 401s once a re-auth redirect is already underway.
        if (hadActiveSession && !reauthRedirectInFlight) {
          const nowMs = Date.now();
          const canReauth =
            typeof window !== "undefined" &&
            shouldReauthenticateOnExpiry(nowMs, readLastReauthAttemptMs());

          if (canReauth) {
            reauthRedirectInFlight = true;
            markReauthAttempt(nowMs);
            window.location.assign(generateLoginUrl());
            return response;
          }

          // We already bounced through Login.gov very recently and still got a 401 (e.g. a
          // revoked/deactivated account that re-auth can't fix). Stop looping and fall back to Home.
          clearReauthAttempt();
          getRouter()?.navigate(HOME_URL, { replace: true });
        }
      } else if (response.status >= 200 && response.status < 300) {
        // Only a successful *authenticated* call proves a prior silent re-auth actually worked. A
        // 2xx from a public endpoint says nothing about the token, so it must not clear the
        // anti-loop guard — otherwise a following 401 looks "fresh" and can re-trigger the redirect
        // loop the guard exists to prevent.
        const wasAuthenticated =
          typeof response.config?.headers?.Authorization === "string" &&
          response.config.headers.Authorization.startsWith("Bearer ");
        if (wasAuthenticated) {
          clearReauthAttempt();
        }
      }

      return response;
    }, function handleResponseError(error) {
      return Promise.reject(error);
    });

    const jwtItem = localStorage.getItem(JWT_TOKEN_KEY);

    try {
      if (!jwtItem) {
        setJwt(null);
        return;
      }

      const jwt = JSON.parse(jwtItem);
      setJwt(jwt);
    } catch (e) {
      setJwt(null);
    }
  }, [])


  const isLoggedIn = useCallback(() => {
    if (!user) {
      return false;
    }

    return true;
  }, [user]);

  const hasAgencyAccess = useCallback((permissions) => {
    const subject = presentedIdentity(user, impersonation);
    if (!subject || !isActiveAccount(subject) || (request?.decisionDate && request.isApproved === false)) {
      return false;
    }

    if (subject.accessLevel === ACCESS_LEVEL_SUPER_USER) {
      return true;
    }

    if (user?.agencyId) {
      setIsAgencyUser(true);
    }

    if (!permissions || isEmpty(permissions)) {
      return true;
    }

    if (!includes(permissions, subject.accessLevel)) {
      return false;
    }

    return true;
  }, [user, request, impersonation]);

  const hasEntityAccess = useCallback((permissions) => {
    const subject = presentedIdentity(user, impersonation);
    if (!subject || !isActiveAccount(subject) || (request?.decisionDate && request?.isApproved === false)) {
      return false;
    }

    if (subject.accessLevel === ACCESS_LEVEL_SUPER_USER) {
      return true;
    }

    // Check if user has either agency or vendor access
    if (!subject.agencyId && !subject.vendorId) {
      return false;
    }

    if (!permissions || isEmpty(permissions)) {
      return true;
    }

    if (!includes(permissions, subject.accessLevel)) {
      return false;
    }

    return true;
  }, [user, request, impersonation]);

  const checkEntityAccessLevel = useCallback((permissions) => {
    const subject = presentedIdentity(user, impersonation);
    if (!subject || !isActiveAccount(subject) || (request?.decisionDate && request.isApproved === false)) {
      return false;
    }

    if (subject.accessLevel === ACCESS_LEVEL_SUPER_USER) {
      return true;
    }

    if (!permissions || isEmpty(permissions)) {
      return true;
    }

    if (!includes(permissions, subject.accessLevel)) {
      return false;
    }

    return true;
  }, [user, request, impersonation]);

  useEffect(() => {
    if (user) {
      const active = isActiveAccount(user);
      setIsVendorUser(!!user.vendorId && active);
      setIsAgencyUser(!!user.agencyId && active);
      setIsSuperUser(user.accessLevel === ACCESS_LEVEL_SUPER_USER && active);
    } else {
      setIsVendorUser(false);
      setIsAgencyUser(false);
      setIsSuperUser(false);
    }

    setHasPendingRequestAccess(!!(request && user && !request.decisionDate));

  }, [user, request]);

  useEffect(() => {
    checkEntityAccessLevel();
  }, [user, request]);

  const pocAddressFieldsEmpty = useCallback((primaryPoc) => {
    const variable = !primaryPoc?.street1 && !primaryPoc?.city && !primaryPoc?.state && !primaryPoc?.zipCode;
    return variable
  }, []);

  const checkFalsyPocFields = useCallback((primaryPoc) => {

    if (!primaryPoc)
      return primaryPoc;

    const localPrimaryPoc = { ...primaryPoc }

    // If all poc address fields are empty, a check in the jsx will display "not supplied." Running the subsequent address ifs will screw with that check.
    if (!pocAddressFieldsEmpty(primaryPoc)) {

      if (!localPrimaryPoc.street1) {
        localPrimaryPoc.street1 = "N/A";
      }

      if (!localPrimaryPoc.street2) {
        localPrimaryPoc.street2 = "N/A";
      }

      if (!localPrimaryPoc.city) {
        localPrimaryPoc.city = "N/A";
      }

      if (!localPrimaryPoc.state) {
        localPrimaryPoc.state = "N/A";
      }

      if (!localPrimaryPoc.zipCode) {
        localPrimaryPoc.zipCode = "N/A";
      }
    }

    // Check other poc fields.
    if (!localPrimaryPoc.phoneNumber) {
      localPrimaryPoc.phoneNumber = "N/A"
    }

    if (!localPrimaryPoc.email) {
      localPrimaryPoc.email = "N/A";
    }

    return localPrimaryPoc

  }, []);

  const refreshMyEntity = useCallback(async () => {
    let entity = null;

    // While impersonating, hydrate the entity from the impersonated org (fetched by id) so every
    // surface that reads userEntity presents that org — not the SuperUser's (empty) own org.
    if (impersonation?.ownerId) {
      const impersonatingVendor = impersonation.ownerType?.toLowerCase() === ENTITY_TYPE_VENDOR;
      if (impersonatingVendor) {
        const vendorResponse = await getVendor(impersonation.ownerId);
        if (vendorResponse && vendorResponse.id) {
          entity = { type: ENTITY_TYPE_VENDOR, entity: vendorResponse };
          if (vendorResponse.primaryPocId) {
            const primaryPoc = await getPrimaryPoc(vendorResponse.primaryPocId);
            entity = { ...entity, primaryPoc };
          }
        }
      } else {
        const agencyResponse = await getAgency(impersonation.ownerId);
        if (agencyResponse && agencyResponse.id) {
          entity = { type: ENTITY_TYPE_AGENCY, entity: agencyResponse };
          if (agencyResponse.primaryPocId) {
            const primaryPoc = await getPrimaryPoc(agencyResponse.primaryPocId);
            entity = { ...entity, primaryPoc };
          }
        }
      }
      setUserEntity(entity);
      return;
    }

    // Check if user is a vendor user first
    if (user?.vendorId) {
      const vendorResponse = await getUserVendor();

      if (vendorResponse && vendorResponse.id) {
        entity = { type: ENTITY_TYPE_VENDOR, entity: vendorResponse };

        if (vendorResponse && vendorResponse.primaryPocId) {
          const primaryPoc = await getPrimaryPoc(vendorResponse.primaryPocId)
          entity = { ...entity, primaryPoc };
        }

        setUserEntity(entity);
      }
    }
    // Otherwise check if user is an agency user
    else if (user?.agencyId) {
      const agencyResponse = await getUserAgency();
      if (agencyResponse && agencyResponse.id) {
        entity = { type: ENTITY_TYPE_AGENCY, entity: agencyResponse };

        if (agencyResponse && agencyResponse.primaryPocId) {
          const primaryPoc = await getPrimaryPoc(agencyResponse.primaryPocId)
          entity = { ...entity, primaryPoc };
        }

        setUserEntity(entity);
      }
    }
  }, [impersonation, user, getVendor, getAgency, getUserVendor, getUserAgency, getPrimaryPoc, setUserEntity]);

  // The real, authenticated SuperUser status (independent of any impersonation presentation).
  const realIsSuperUser = !!user && isActiveAccount(user) && user.accessLevel === ACCESS_LEVEL_SUPER_USER;
  const isImpersonating = !!impersonation && realIsSuperUser;

  // Presented ("effective") identity. While impersonating, the app behaves as an admin of the target
  // org: downstream branches on user.vendorId/agencyId, isVendorUser/isAgencyUser and isSuperUser all
  // resolve to the org. The real SuperUser token still authorizes every request server-side, and the
  // server independently derives the acting org from the active session — this is presentation only.
  const impersonatingVendor = isImpersonating && impersonation.ownerType?.toLowerCase() === ENTITY_TYPE_VENDOR;
  const impersonatingAgency = isImpersonating && impersonation.ownerType?.toLowerCase() === ENTITY_TYPE_AGENCY;

  const effectiveUser = useMemo(
    () => presentedIdentity(user, impersonation),
    [user, impersonation]
  );

  const effectiveIsVendorUser = isImpersonating ? impersonatingVendor : isVendorUser;
  const effectiveIsAgencyUser = isImpersonating ? impersonatingAgency : isAgencyUser;
  const effectiveIsSuperUser = isImpersonating ? false : isSuperUser;

  const startImpersonation = useCallback(async (ownerType, ownerId) => {
    try {
      const response = await axios.post(`${impersonationApiBase}/start`, { ownerType, ownerId });
      if (response.status >= 200 && response.status < 300 && response.data) {
        const session = {
          ownerType: response.data.ownerType,
          ownerId: response.data.ownerId,
          ownerName: response.data.ownerName,
          expiresAt: response.data.expiresAt,
        };
        try { sessionStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(session)); } catch { /* ignore */ }
        setImpersonation(session);
        return { success: true, session };
      }
      return { success: false, status: response.status };
    } catch (e) {
      return { success: false, status: e?.response?.status };
    }
  }, [impersonationApiBase]);

  const endImpersonation = useCallback(async () => {
    try { await axios.post(`${impersonationApiBase}/end`); } catch { /* best effort */ }
    try { sessionStorage.removeItem(IMPERSONATION_SESSION_KEY); } catch { /* ignore */ }
    setImpersonation(null);
    setUserEntity(null);
  }, [impersonationApiBase, setUserEntity]);

  // Restore any active impersonation session on load / login. sessionStorage gives an immediate
  // optimistic value; the server response is authoritative (204 => none active, and any stale cache
  // is cleared). Only SuperUsers can have a session.
  useEffect(() => {
    if (!realIsSuperUser) {
      if (impersonation) {
        setImpersonation(null);
      }
      return undefined;
    }

    let cancelled = false;
    try {
      const cached = sessionStorage.getItem(IMPERSONATION_SESSION_KEY);
      if (cached) {
        setImpersonation(JSON.parse(cached));
      }
    } catch { /* ignore */ }

    (async () => {
      try {
        const response = await axios.get(`${impersonationApiBase}/current`);
        if (cancelled) { return; }
        if (response.status === 200 && response.data) {
          const session = {
            ownerType: response.data.ownerType,
            ownerId: response.data.ownerId,
            ownerName: response.data.ownerName,
            expiresAt: response.data.expiresAt,
          };
          try { sessionStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(session)); } catch { /* ignore */ }
          setImpersonation(session);
        } else {
          try { sessionStorage.removeItem(IMPERSONATION_SESSION_KEY); } catch { /* ignore */ }
          setImpersonation(null);
        }
      } catch { /* leave optimistic cache in place on transient errors */ }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realIsSuperUser, impersonationApiBase]);

  // Hydrate the impersonated org's entity whenever the active session changes.
  useEffect(() => {
    if (impersonation?.ownerId) {
      refreshMyEntity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonation?.ownerId, impersonation?.ownerType]);



  const getEntityPrimaryPocAddress = useCallback(() => { return formatPrimaryPoc(userEntity?.primaryPoc?.street1, userEntity?.primaryPoc?.street2, userEntity?.primaryPoc?.city, userEntity?.primaryPoc?.state, userEntity?.primaryPoc?.zipCode); }, [userEntity])
  const contextValue = useMemo(() => ({
    token,
    // Effective (presented) identity — the impersonated org while impersonating, else the real user.
    user: effectiveUser,
    authError,
    setAuthError,
    setUser,
    setJwt,
    request,
    setRequest,
    hasPendingRequestAccess,
    checkEntityAccessLevel,
    hasAgencyAccess,
    hasEntityAccess,
    userEntity,
    setUserEntity,
    getEntityPrimaryPocAddress,
    checkFalsyPocFields,
    pocAddressFieldsEmpty,
    isLoggedIn,
    isVendorUser: effectiveIsVendorUser,
    isAgencyUser: effectiveIsAgencyUser,
    isSuperUser: effectiveIsSuperUser,
    refreshMyEntity,
    // Real identity + impersonation controls (used by SuperUser Tools, the overlay, and nav gating).
    realUser: user,
    realIsSuperUser,
    isImpersonating,
    impersonation,
    startImpersonation,
    endImpersonation,
    initialUserStorageCheck,
    initialUserCallbackCheck,
    setInitialUserStorageCheck,
    setInitialUserCallbackCheck
  }), [token,
    setToken,
    user,
    effectiveUser,
    authError,
    setUser,
    request,
    userEntity,
    hasPendingRequestAccess,
    effectiveIsVendorUser,
    effectiveIsAgencyUser,
    effectiveIsSuperUser,
    realIsSuperUser,
    isImpersonating,
    impersonation,
    startImpersonation,
    endImpersonation,
    initialUserStorageCheck,
    initialUserCallbackCheck]);

  return (
    <UserContext.Provider value={contextValue} >
      {children}
    </UserContext.Provider>
  );
}

